import { createContext, useContext, useMemo, type ReactNode } from "react";
import { Effect, Match } from "effect";
import { Atom, AsyncResult } from "effect/unstable/reactivity";
import { useAtomValue } from "@effect/atom-react";
import type { FrontendRoot } from "../state/root-owner.ts";
import { QueryWork } from "../state/query-work.ts";
import type { ImmutableRegistryFailure } from "./registry-model.ts";
import type { CodeDiffConfiguration } from "./diff-model.ts";
import type { CodeDiffEditorPropsCallback } from "./diff-editor-io.ts";
import { DiffEditors, type DiffEditorSession } from "./diff-editor-service.ts";

export interface DiffEditorRoot { readonly atoms: FrontendRoot<DiffEditors>["atoms"] }
const DiffEditorContext = createContext<DiffEditorRoot | undefined>(undefined);
export const DiffEditorsProvider = ({ value, children }: { readonly value: DiffEditorRoot; readonly children: ReactNode }) => <DiffEditorContext.Provider value={value}>{children}</DiffEditorContext.Provider>;

export function useDiffEditor(container: HTMLElement | null, identity: string, configuration: CodeDiffConfiguration, mounted: CodeDiffEditorPropsCallback): string | null {
  const root = useContext(DiffEditorContext);
  const editor = useMemo<Atom.Atom<AsyncResult.AsyncResult<DiffEditorSession | undefined, ImmutableRegistryFailure>>>(() => root && container ? root.atoms.atom(Effect.gen(function* () {
    const lease = yield* (yield* DiffEditors).acquire(identity, container, configuration.mode, configuration.label, mounted);
    return lease.model;
  })).pipe(Atom.setIdleTTL(0)) : Atom.make(Effect.succeed(undefined)), [root, container, identity, mounted]);
  const acquired = useAtomValue(editor);
  const session = Match.value(acquired).pipe(Match.tag("Failure", () => undefined), Match.tag("Initial", () => undefined), Match.tag("Success", state => state.value), Match.exhaustive);
  const update = useMemo(() => root && session ? root.atoms.atom(session.update(configuration)).pipe(Atom.setIdleTTL(0)) : Atom.make(Effect.succeed({ message: null })), [root, session,
    configuration.candidateId, configuration.active,
    configuration.original.contents, configuration.original.path, configuration.original.treeId, configuration.original.workspaceId,
    configuration.modified.contents, configuration.modified.path, configuration.modified.treeId, configuration.modified.workspaceId]);
  const updated = useAtomValue(update);
  const configure = useMemo(() => root && session ? root.atoms.atom(Effect.gen(function* () {
    return yield* (yield* QueryWork).observe(session.configure(configuration));
  })).pipe(Atom.setIdleTTL(0)) : Atom.make(Effect.void), [root, session, configuration.mode, configuration.label, configuration.active, configuration.revealLine,
    configuration.candidateId, configuration.original.contents, configuration.original.path, configuration.original.treeId, configuration.original.workspaceId,
    configuration.modified.contents, configuration.modified.path, configuration.modified.treeId, configuration.modified.workspaceId]);
  const configured = useAtomValue(configure);
  if (container && !root) return "Diff model failed.";
  if (configured._tag === "Failure") return "Diff model failed.";
  return Match.value(acquired).pipe(
    Match.tag("Failure", () => "Diff model failed."),
    Match.tag("Initial", () => null),
    Match.tag("Success", () => Match.value(updated).pipe(
      Match.tag("Failure", () => "Diff model failed."),
      Match.tag("Initial", () => null),
      Match.tag("Success", state => state.value.message),
      Match.exhaustive
    )),
    Match.exhaustive
  );
}
